<script lang="ts" setup>
import { computed, ref, toRef } from 'vue'
import type { BookingFormValidationResult } from './bookingTypes'
import type { Container } from '~/models/containers'
import type { GETAutoBookRequest } from '~/services/apiClient'
import AutoBookingTableRow from '~/components/inputs/AutoBookingTableRow.vue'
import type { AppointmentSlot } from '~/models/appointmentSlot'
import { useContainerBooking } from '~/compositions/useContainerBooking'
import { TERMINALS_REQUIRING_MASTER_BL } from '~/constants'

const props = defineProps<{
  containers: Container[]
  existingSettings?: GETAutoBookRequest
  appointmentSlot?: AppointmentSlot
}>()

const rowRefs = ref<Array<InstanceType<typeof AutoBookingTableRow> | null>>([])
const existingSettingsRef = toRef(props, 'existingSettings')
const containersWithForm = computed(() =>
  props.containers.map((container) => {
    const booking = useContainerBooking({
      container,
      appointmentSlot: props.appointmentSlot,
      existingSettings: existingSettingsRef,
    })
    return {
      container,
      ...booking,
    }
  })
)

async function validate(): Promise<BookingFormValidationResult[]> {
  const results: BookingFormValidationResult[] = []
  for (const rowRef of rowRefs.value) {
    if (rowRef) {
      const validation = await rowRef.validate()
      results.push(validation)
    }
  }
  return results
}

defineExpose({
  validate,
})
</script>

<template>
  <div class="table-container">
    <table class="booking-table">
      <thead>
        <tr>
          <th class="container-number">Container #</th>
          <th class="terminal">Terminal</th>
          <th class="lfd">LFD</th>
          <th class="status">Existing Status</th>
          <th v-if="TERMINALS_REQUIRING_MASTER_BL.size > 0" class="master-bl">
            Master BL #
          </th>
          <th class="chassis">Chassis</th>
          <th class="license-plate">License Plate #</th>
        </tr>
      </thead>
      <tbody>
        <AutoBookingTableRow
          v-for="(row, i) in containersWithForm"
          :key="row.container.number"
          :ref="
            (el) =>
              (rowRefs[i] = el as InstanceType<typeof AutoBookingTableRow>)
          "
          :container="row.container"
          :existing-settings="existingSettingsRef"
          :appointment-slot="appointmentSlot"
        />
      </tbody>
    </table>
  </div>
</template>

<style lang="scss">
.booking-table {
  td,
  th {
    text-align: left;
  }
  th {
    padding-left: 0px !important;
  }
}
</style>

<style scoped>
.table-container {
  padding-bottom: 20px;
  max-height: 40vh;
  overflow-y: auto;
}
.booking-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.booking-table thead th {
  background-color: #d4d7dd;
  color: #434854;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 1;
  height: 32px;
}
.booking-table tbody td {
  background-color: #ffffff;
  padding: 1px 8px;
}
.booking-table tbody tr {
  border-bottom: 1px solid #e0e0e0;
}
.booking-table tbody tr:nth-child(even) td {
  background-color: #f6f6f6;
}
.container-number {
  width: 150px;
}
.terminal {
  width: 100px;
}
.lfd {
  width: 65px;
}
.status {
  width: 120px;
}
.master-bl {
  width: 190px;
}
.chassis {
  width: 90px;
}
.license-plate {
  width: auto;
}
</style>
